import React from 'react';
import { View, Text, Pressable } from 'react-native';
import Input from '@/components/General/inputs/Input';
import Unicon from '@/components/General/Unicon';
import { StyleSheet } from 'react-native-unistyles';

export default function CollapsibleClassCard({ cls, index, isExpanded, onToggle, onUpdate, onRemove, onAddRequirement, onUpdateRequirement, onRemoveRequirement }) {
    const reqCount = cls.Requirements?.length || 0;
    const hasRequirements = reqCount > 0;

    if (!isExpanded) {
        // Collapsed view - compact summary
        return (
            <Pressable onPress={onToggle} style={styles.classCardCollapsed}>
                <View style={styles.classCardCollapsedLeft}>
                    <View style={styles.classNumberBadge}>
                        <Text style={styles.classNumberText}>{index + 1}</Text>
                    </View>
                    <View style={styles.classCardCollapsedInfo}>
                        <Text style={styles.classCardCollapsedName} numberOfLines={1}>
                            {cls.name?.trim() || 'Untitled Class'}
                        </Text>
                        <Text style={styles.classCardCollapsedMeta}>
                            {hasRequirements ? `${reqCount} requirement${reqCount !== 1 ? 's' : ''}` : 'No requirements'}
                            {cls.description?.trim() ? ' • Has description' : ''}
                        </Text>
                    </View>
                </View>
                <View style={styles.classCardCollapsedActions}>
                    <Pressable onPress={(e) => { e.stopPropagation(); onRemove(); }} hitSlop={8}>
                        <Unicon name="trash-outline" size={18} colorVariant="error" />
                    </Pressable>
                    <Unicon name="chevron-down-outline" size={18} colorVariant="textSecondary" />
                </View>
            </Pressable>
        );
    }

    // Expanded view - full editing
    return (
        <View style={styles.classCardExpanded}>
            <Pressable onPress={onToggle} style={styles.classCardExpandedHeader}>
                <View style={styles.classCardExpandedLeft}>
                    <View style={styles.classNumberBadgeLarge}>
                        <Text style={styles.classNumberTextLarge}>{index + 1}</Text>
                    </View>
                    <Text style={styles.classCardExpandedTitle}>Editing Class {index + 1}</Text>
                </View>
                <View style={styles.classCardExpandedActions}>
                    <Pressable onPress={(e) => { e.stopPropagation(); onRemove(); }} hitSlop={8}>
                        <Unicon name="trash-outline" size={18} colorVariant="error" />
                    </Pressable>
                    <Unicon name="chevron-up-outline" size={18} colorVariant="textSecondary" />
                </View>
            </Pressable>

            <View style={styles.classCardExpandedContent}>
                <Input
                    title="Class Name"
                    placeholder="e.g. Lesson 1"
                    maxLength={120}
                    value={cls.name}
                    onUpdate={(v) => onUpdate('name', v)}
                />
                <Input
                    title="Description (optional)"
                    multiline
                    placeholder="Brief description of this class..."
                    value={cls.description}
                    onUpdate={(v) => onUpdate('description', v)}
                />

                {/* Requirements Section */}
                <View style={styles.requirementsSection}>
                    <Text style={styles.requirementsSectionTitle}>
                        Requirements {reqCount > 0 && `(${reqCount})`}
                    </Text>
                    
                    {cls.Requirements?.map((req, ri) => (
                        <View key={req.id || req.tempId || ri} style={styles.requirementRow}>
                            <View style={styles.requirementNumberBadge}>
                                <Text style={styles.requirementNumberText}>{ri + 1}</Text>
                            </View>
                            <View style={styles.requirementInputs}>
                                <Input
                                    title="Name"
                                    placeholder="e.g. Quiz"
                                    maxLength={120}
                                    value={req.name}
                                    onUpdate={(v) => onUpdateRequirement(ri, 'name', v)}
                                />
                                <Input
                                    title="Description (optional)"
                                    multiline
                                    placeholder="Brief description..."
                                    value={req.description}
                                    onUpdate={(v) => onUpdateRequirement(ri, 'description', v)}
                                />
                            </View>
                            <Pressable onPress={() => onRemoveRequirement(ri)} hitSlop={8} style={styles.removeReqButton}>
                                <Unicon name="close-outline" size={18} colorVariant="error" />
                            </Pressable>
                        </View>
                    ))}

                    <Pressable onPress={onAddRequirement} style={styles.addRequirementButton}>
                        <Unicon name="add-outline" size={14} colorVariant="primary" />
                        <Text style={styles.addRequirementButtonText}>Add Requirement</Text>
                    </Pressable>
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    // Collapsed Class Card
    classCardCollapsed: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: colors.surface,
        borderRadius: 10,
        padding: spacing.md,
        marginBottom: spacing.sm,
        borderWidth: 1,
        borderColor: colors.border,
        _web: { transition: 'all 0.15s ease', cursor: 'pointer', _hover: { backgroundColor: colors.hover } }
    },
    classCardCollapsedLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        flex: 1,
    },
    classNumberBadge: {
        width: 28,
        height: 28,
        borderRadius: 8,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    classNumberText: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    classCardCollapsedInfo: {
        flex: 1,
        gap: 2,
    },
    classCardCollapsedName: {
        fontSize: typography.body,
        fontFamily: fonts.medium,
        color: colors.text,
    },
    classCardCollapsedMeta: {
        fontSize: 11,
        color: colors.textSecondary,
    },
    classCardCollapsedActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },

    // Expanded Class Card
    classCardExpanded: {
        backgroundColor: colors.surface,
        borderRadius: 10,
        marginBottom: spacing.sm,
        borderWidth: 1,
        borderColor: colors.primary,
        overflow: 'hidden',
    },
    classCardExpandedHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: spacing.md,
        backgroundColor: colors.primaryContainer,
        _web: { cursor: 'pointer' }
    },
    classCardExpandedLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    classNumberBadgeLarge: {
        width: 32,
        height: 32,
        borderRadius: 10,
        backgroundColor: colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    classNumberTextLarge: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.onPrimary,
    },
    classCardExpandedTitle: {
        fontSize: typography.body,
        fontFamily: fonts.medium,
        color: colors.primary,
    },
    classCardExpandedActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    classCardExpandedContent: {
        padding: spacing.md,
        gap: spacing.md,
    },

    // Requirements Section (within expanded card)
    requirementsSection: {
        marginTop: spacing.sm,
        paddingTop: spacing.md,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        gap: spacing.sm,
    },
    requirementsSectionTitle: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        marginBottom: spacing.xs,
    },
    requirementRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: spacing.xs,
        backgroundColor: colors.background,
        borderRadius: 8,
        padding: spacing.sm,
    },
    requirementNumberBadge: {
        width: 22,
        height: 22,
        borderRadius: 6,
        backgroundColor: colors.surfaceVariant,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 2,
    },
    requirementNumberText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
    },
    requirementInputs: {
        flex: 1,
        gap: spacing.xs,
    },
    removeReqButton: {
        padding: 4,
        marginTop: 2,
    },
    addRequirementButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.md,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: colors.primary,
        borderStyle: 'dashed',
        alignSelf: 'flex-start',
        marginTop: spacing.xs,
    },
    addRequirementButtonText: {
        fontSize: 12,
        color: colors.primary,
        fontFamily: fonts.medium,
    },
}));
